/**
 * Universal Search Modal Component
 * Full-screen modal search interface with context awareness
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Search, X, ArrowRight, Clock, FileText, Briefcase, Package } from 'lucide-react'
import { useSearch } from '../contexts/SearchContext'
import { jobsService } from '../services/api'
import { projectService } from '../services/projectApi'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { debounce } from 'lodash'

interface SearchContext {
  path: string
  label: string
  placeholder: string
  icon: React.ComponentType<any>
  searchKeys: string[]
}

interface SearchResult {
  id: string
  title: string
  subtitle?: string
  type: 'project' | 'job' | 'metric' | 'client'
  icon?: React.ComponentType<any>
  path?: string
  value?: string | number
  metadata?: Record<string, any>
}

const searchContexts: SearchContext[] = [
  // {
  //   path: '/',
  //   label: 'Everything',
  //   placeholder: 'Search across all data...',
  //   icon: Search,
  //   searchKeys: ['metric', 'kpi', 'report', 'project', 'job', 'client']
  // },
  {
    path: '/projects',
    label: 'Projects',
    placeholder: 'Search projects by name, ID, or client...',
    icon: FileText,
    searchKeys: ['name', 'project_id', 'client', 'sector']
  },
  {
    path: '/jobs',
    label: 'Jobs',
    placeholder: 'Search jobs by ID, name, or status...',
    icon: Briefcase,
    searchKeys: ['job_id', 'name', 'status', 'type']
  }
]


interface UniversalSearchModalProps {
  isOpen: boolean
  onClose: () => void
  onSearch?: (query: string, context: string) => void
}

const UniversalSearchModal: React.FC<UniversalSearchModalProps> = ({ isOpen, onClose, onSearch }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { searchQuery, setSearchQuery: setGlobalSearchQuery, setSearchContext } = useSearch()
  const [query, setQuery] = useState(searchQuery || '')
  const [selectedContext, setSelectedContext] = useState<SearchContext | null>(null)
  const [recentSearches, setRecentSearches] = useState<{query: string, context: string}[]>([])
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Fetch individual jobs data with search query (no date filter - search all time)
  const { data: jobsData } = useQuery({
    queryKey: ['jobsListModalAllTime', query],
    queryFn: () => jobsService.listJobs({
      page: 1,
      pageSize: 50,
      search: query.trim() || null,
      sortBy: 'variance_percent',
      sortOrder: 'desc'
    }),
    enabled: isOpen && selectedContext?.path === '/jobs' && !!query.trim(),
    staleTime: 5000
  })

  // Fetch projects data with search query (no date filter - search all time)
  const { data: projectsData } = useQuery({
    queryKey: ['projectsListModalAllTime', query],
    queryFn: async () => {
      try {
        // Temporarily clear global date range to search all time
        const savedDateRange = (window as any).__CORTEX_DATE_RANGE__
        delete (window as any).__CORTEX_DATE_RANGE__

        const response = await projectService.getProjects(
          { search: query.trim() },
          { field: 'created_date', direction: 'desc' },
          { page: 1, pageSize: 50 }
        )

        // Restore global date range
        if (savedDateRange) {
          (window as any).__CORTEX_DATE_RANGE__ = savedDateRange
        }

        return response
      } catch (error) {
        console.error('Error fetching projects:', error)
        return null
      }
    },
    enabled: isOpen && selectedContext?.path === '/projects' && !!query.trim(),
    staleTime: 5000
  })

  // Process search results when data changes or query changes
  useEffect(() => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    const results: SearchResult[] = []

    if (selectedContext?.path === '/jobs' && jobsData) {
      // Process individual jobs from API response
      if (jobsData.jobs && Array.isArray(jobsData.jobs)) {
        jobsData.jobs.forEach((job: any) => {
          results.push({
            id: job.job_id,
            title: `${job.job_id} - ${job.part_description || job.project_name || ''}`,
            subtitle: `${job.customer_name || 'Unknown'} - ${job.status} - Est: £${(job.estimated_cost || 0).toLocaleString('en-GB')} / Actual: £${(job.actual_cost || 0).toLocaleString('en-GB')}`,
            type: job.job_type === 'STOCK' ? 'job' : 'project',
            icon: job.job_type === 'STOCK' ? Package : Briefcase,
            // Navigate to job detail page
            path: `/jobs/${job.job_id}`,
            value: job.actual_cost
          })
        })

        // Limit results to top 10
        if (results.length > 10) {
          results.splice(10)
        }
      }
    } else if (selectedContext?.path === '/projects' && projectsData) {
      // Process projects data from API (filtered by search, no date restriction)
      if (projectsData.projects && Array.isArray(projectsData.projects)) {
        projectsData.projects.forEach((project: any) => {
          const projectName = project.name || ''
          const customerName = project.customer_name || ''
          const projectId = project.id || ''

          results.push({
            id: projectId,
            title: `${projectId} - ${projectName}`,
            subtitle: `${customerName} - ${project.status} - £${(project.quoted_amount || 0).toLocaleString('en-GB')}`,
            type: 'project',
            icon: FileText,
            path: `/projects/${projectId}`,
            value: project.quoted_amount
          })
        })

        // Limit results to top 10
        if (results.length > 10) {
          results.splice(10)
        }
      }
    }

    setSearchResults(results)
  }, [query, jobsData, projectsData, selectedContext])

  // Get current search context based on location
  const getCurrentContext = () => {
    // Check if we're on jobs page (any view)
    if (location.pathname.startsWith('/jobs')) {
      return searchContexts.find(ctx => ctx.path === '/jobs')
    }
    // Check if we're on projects page
    if (location.pathname.startsWith('/projects')) {
      return searchContexts.find(ctx => ctx.path === '/projects')
    }
    // Default to Projects (first item since we removed Everything)
    return searchContexts[0]
  }

  // Initialize selected context when modal opens
  useEffect(() => {
    if (isOpen) {
      const context = getCurrentContext()
      setSelectedContext(context || searchContexts[0])

      // If we're on jobs page but not on performance view, navigate to performance view
      if (location.pathname === '/jobs') {
        const currentParams = new URLSearchParams(location.search)
        const currentView = currentParams.get('view')

        if (currentView !== 'performance') {
          // Build new params
          const params = new URLSearchParams()
          params.set('view', 'performance')

          // Preserve search query if it exists
          const searchQuery = currentParams.get('search')
          if (searchQuery) {
            params.set('search', searchQuery)
          }

          const targetPath = `/jobs?${params.toString()}`

          // Use navigate with state to force update
          navigate(targetPath, { replace: false, state: { fromModal: true } })
        }
      }
    }
  }, [isOpen, location.pathname, location.search, navigate])

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('universalRecentSearches')
    if (stored) {
      setRecentSearches(JSON.parse(stored).slice(0, 8))
    }
  }, [])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
      setQuery(searchQuery || '')
    }
  }, [isOpen, searchQuery])

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value: string, context: SearchContext) => {
      if (!value.trim()) {
        setIsSearching(false)
        return
      }

      setIsSearching(true)

      // Update global search context
      setGlobalSearchQuery(value)
      setSearchContext(context.path)

      // Call external search handler
      if (onSearch) {
        onSearch(value, context.path)
      }

      // Store search in recent searches
      const newSearch = { query: value, context: context.path }
      const searches = [newSearch, ...recentSearches.filter(s =>
        !(s.query === value && s.context === context.path)
      )].slice(0, 8)
      setRecentSearches(searches)
      localStorage.setItem('universalRecentSearches', JSON.stringify(searches))

      // Navigate to the page with search query in the background
      const params = new URLSearchParams()
      params.set('search', value)

      // For Jobs page, default to performance view
      if (context.path === '/jobs') {
        params.set('view', 'performance')
      }

      const queryString = params.toString()
      const targetPath = queryString ? `${context.path}?${queryString}` : context.path

      // Navigate in the background (modal stays open)
      navigate(targetPath)

      // Set searching to false after a short delay
      setTimeout(() => setIsSearching(false), 300)
    }, 300),
    [recentSearches, setGlobalSearchQuery, setSearchContext, onSearch, navigate]
  )

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setQuery(value)
    if (selectedContext) {
      debouncedSearch(value, selectedContext)
    }
  }

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim() && selectedContext) {
      debouncedSearch.flush()
      // Navigate to the selected context with search
      if (location.pathname !== selectedContext.path) {
        navigate(selectedContext.path)
      }
      onClose()
    }
  }

  // Handle context switch with immediate navigation
  const handleContextSwitch = async (context: SearchContext) => {
    setSelectedContext(context)

    // Build the URL with appropriate parameters
    const params = new URLSearchParams()

    // Add search query if present
    if (query) {
      params.set('search', query)
    }

    // For Jobs page, always go to performance view for search
    if (context.path === '/jobs') {
      params.set('view', 'performance')
    }

    const queryString = params.toString()
    const targetPath = queryString ? `${context.path}?${queryString}` : context.path

    // Always navigate when switching context (even if already on that page but different view)
    navigate(targetPath)

    if (query) {
      debouncedSearch(query, context)
    }
  }

  // Handle recent search click
  const handleRecentSearchClick = (recent: {query: string, context: string}) => {
    const context = searchContexts.find(c => c.path === recent.context) || getCurrentContext()
    if (context) {
      setSelectedContext(context)
      setQuery(recent.query)
      debouncedSearch(recent.query, context)
    }
  }

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    if (result.path) {
      navigate(result.path)
      onClose()
    }
  }


  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Ultra-minimal backdrop with subtle blur for maximum visibility */}
      <div
        className="absolute inset-0 bg-black/5 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal with stronger border and glow effect */}
      <div className="relative flex items-start justify-center pt-20">
        <div className="w-full max-w-3xl mx-4 bg-white dark:bg-slate-900 rounded-xl overflow-hidden border-2 border-slate-300 dark:border-slate-600 shadow-[0_20px_70px_-15px_rgba(0,0,0,0.5)] dark:shadow-[0_20px_70px_-15px_rgba(0,0,0,0.8)] ring-1 ring-white/20 dark:ring-white/10">
          {/* Search Header */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-800">
            <form onSubmit={handleSubmit}>
              <div className="flex items-center space-x-3">
                <Search className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={handleInputChange}
                  placeholder={selectedContext?.placeholder || 'Search...'}
                  className="flex-1 text-lg bg-transparent outline-none text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('')
                      setSearchResults([])
                      inputRef.current?.focus()
                    }}
                    className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
              </div>
            </form>

            {/* Context Switcher */}
            <div className="flex items-center space-x-2 mt-3">
              <span className="text-xs text-slate-500 dark:text-slate-400">Search in:</span>
              {searchContexts.map(context => {
                const Icon = context.icon
                const isActive = selectedContext?.path === context.path ||
                                (selectedContext?.path === context.path && location.pathname === context.path)

                return (
                  <button
                    key={context.path}
                    onClick={() => handleContextSwitch(context)}
                    className={clsx(
                      'relative flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                      isActive
                        ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{context.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Search Results / Suggestions - Fixed height container */}
          <div className="h-96 overflow-y-auto">
            {isSearching ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center space-x-2 text-slate-500 dark:text-slate-400">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 dark:border-slate-600 border-t-blue-600 dark:border-t-blue-400"></div>
                  <span className="text-sm">Searching...</span>
                </div>
              </div>
            ) : query && searchResults.length > 0 ? (
              <div>
                {/* Top Results Section */}
                <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                  <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Search Results</span>
                </div>
                <div className="py-2">
                  {searchResults.map((result) => {
                    const Icon = result.icon || FileText
                    return (
                      <button
                        key={result.id}
                        onClick={() => handleResultClick(result)}
                        className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors group"
                      >
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-500/20 rounded-lg flex items-center justify-center">
                          <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-medium text-slate-900 dark:text-white">
                            {result.title}
                          </div>
                          {result.subtitle && (
                            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              {result.subtitle}
                            </div>
                          )}
                        </div>
                        {result.value && (
                          <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                            {typeof result.value === 'number' ? result.value.toLocaleString() : result.value}
                          </div>
                        )}
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-xs text-slate-400 dark:text-slate-500">Select</span>
                          <ArrowRight className="w-3 h-3 text-slate-400 dark:text-slate-500 inline ml-1" />
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : query && searchResults.length === 0 && !isSearching ? (
              // Show "no results" message
              <div className="p-8 text-center">
                <div className="mb-4">
                  <Search className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
                </div>
                <div className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                  No results for "{query}"
                </div>
                <div className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                  Try a different search term
                </div>
              </div>
            ) : !query && recentSearches.length > 0 ? (
              <div className="py-2">
                <div className="px-4 py-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                  Recent searches
                </div>
                {recentSearches.map((recent, idx) => {
                  const context = searchContexts.find(c => c.path === recent.context)
                  const Icon = context?.icon || Clock
                  return (
                    <button
                      key={idx}
                      onClick={() => handleRecentSearchClick(recent)}
                      className="w-full px-4 py-3 flex items-center space-x-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <Icon className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                      <div className="flex-1 text-left">
                        <div className="text-sm text-slate-700 dark:text-slate-300">{recent.query}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">in {context?.label}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                    </button>
                  )
                })}
              </div>
            ) : !query ? (
              <div className="p-8 text-center">
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  Start typing to search across {selectedContext?.label.toLowerCase()}
                </div>
              </div>
            ) : null}
          </div>

          {/* Keyboard hints footer */}
          <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-900 rounded border border-slate-300 dark:border-slate-600">Tab</kbd>
                  <span>to navigate</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-900 rounded border border-slate-300 dark:border-slate-600">↵</kbd>
                  <span>to select</span>
                </div>
                <div className="flex items-center space-x-1">
                  <kbd className="px-1.5 py-0.5 bg-white dark:bg-slate-900 rounded border border-slate-300 dark:border-slate-600">ESC</kbd>
                  <span>to close</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-slate-400">Powered by</span>
                <span className="font-medium text-slate-600 dark:text-slate-300">Therion Cortex</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

export default UniversalSearchModal